import { useCallback, useEffect, useState, useRef } from 'react';
import { useNotifications } from '@toolpad/core/useNotifications';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { functions, db, auth } from 'src/firebase/firebaseConfig';
import { useSelector } from 'src/store';
import { EmailTemplate, EmailOptions, NotificationsRequestData, SendNotificationResult } from 'shared/types';
import { DayliteContact } from 'shared/types/dayliteModels';

export interface ClientInviteRecord {
  sentCount: number;
  lastSentAt: Timestamp | null;
  history: Array<{ sentAt: Timestamp; sentBy: string }>;
}

export interface ClientAuthStatus {
  [email: string]: boolean;
}

interface UseClientNotificationsOptions {
  eventId: string | undefined;
  contactsData: Record<string, DayliteContact>;
  contactIds: string[];
}

export default function useClientNotifications({
  eventId,
  contactsData,
  contactIds,
}: UseClientNotificationsOptions) {
  const notifications = useNotifications();
  const emailTemplates = useSelector((state) => state.staffGlobal.emailTemplates);
  const mainData = useSelector((state) => state.activeEvent.mainData);

  const sendNotificationFunction = httpsCallable<
    NotificationsRequestData,
    { success: boolean; data: SendNotificationResult }
  >(functions, 'sendNotificationsByIds');

  const checkUsersExistFunction = httpsCallable<
    { emails: string[] },
    { success: boolean; results: Record<string, boolean> }
  >(functions, 'checkUsersExist');

  // Invite tracking state: contactId -> record
  const [inviteRecords, setInviteRecords] = useState<Record<string, ClientInviteRecord>>({});
  const [authStatuses, setAuthStatuses] = useState<ClientAuthStatus>({});
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [sendingContactId, setSendingContactId] = useState<string | null>(null);

  // Track which IDs we've already loaded to avoid duplicate fetches
  const loadedInviteIdsRef = useRef<Set<string>>(new Set());
  const checkedEmailsRef = useRef<Set<string>>(new Set());

  // Load invite records for all contacts
  useEffect(() => {
    if (!eventId || contactIds.length === 0) return;

    // Only fetch IDs we haven't loaded yet
    const newIds = contactIds.filter((id) => !loadedInviteIdsRef.current.has(id));
    if (newIds.length === 0) return;

    const fetchInviteRecords = async () => {
      setIsLoadingInvites(true);
      try {
        const records: Record<string, ClientInviteRecord> = {};
        await Promise.all(
          newIds.map(async (contactId) => {
            const inviteRef = doc(db, 'events', eventId, 'clientInvites', contactId);
            const inviteSnap = await getDoc(inviteRef);
            if (inviteSnap.exists()) {
              records[contactId] = inviteSnap.data() as ClientInviteRecord;
            }
            loadedInviteIdsRef.current.add(contactId);
          })
        );
        setInviteRecords((prev) => ({ ...prev, ...records }));
      } catch (error) {
        console.error('Error fetching invite records:', error);
      } finally {
        setIsLoadingInvites(false);
      }
    };

    fetchInviteRecords();
  }, [eventId, contactIds]);

  // Check Firebase Auth status for contact emails
  useEffect(() => {
    if (contactIds.length === 0 || Object.keys(contactsData).length === 0) return;

    // Collect emails we haven't checked yet
    const emailsToCheck: string[] = [];
    contactIds.forEach((id) => {
      const contact = contactsData[id];
      if (!contact) return;
      const email =
        contact.emails?.find((e) => e.is_primary)?.address || contact.emails?.[0]?.address;
      if (email && !checkedEmailsRef.current.has(email)) {
        emailsToCheck.push(email);
      }
    });

    if (emailsToCheck.length === 0) return;

    const checkAuth = async () => {
      setIsCheckingAuth(true);
      try {
        const result = await checkUsersExistFunction({ emails: emailsToCheck });
        if (result.data.success) {
          emailsToCheck.forEach((e) => checkedEmailsRef.current.add(e));
          setAuthStatuses((prev) => ({ ...prev, ...result.data.results }));
        }
      } catch (error) {
        console.error('Error checking auth statuses:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [contactIds, contactsData]);

  // Build the onboarding email from the "Client Onboarding" template
  const buildOnboardingEmail = useCallback((): EmailTemplate | null => {
    const template = emailTemplates.find((t) => t.name === 'Client Onboarding');
    if (!template) {
      notifications.show('Email template "Client Onboarding" not found', {
        severity: 'error',
        autoHideDuration: 4000,
      });
      return null;
    }

    const processField = (field: string) =>
      field
        .replace(/\[event-date\]/g, mainData?.titleExtractedDate || '')
        .replace(/\[event-title\]/g, mainData?.friendlyTitle || '');

    const result: EmailTemplate = { name: template.name };
    if (template.subject) result.subject = processField(template.subject);
    if (template.html) result.html = processField(template.html);
    if (template.text) result.text = processField(template.text);

    return result;
  }, [emailTemplates, mainData, notifications]);

  // Send onboarding invite to a single contact
  const sendOnboardingInvite = useCallback(
    async (contactId: string) => {
      if (!eventId) return;

      const template = buildOnboardingEmail();
      if (!template) return;

      setSendingContactId(contactId);
      try {
        const userEmail = auth.currentUser?.email || '';

        const emailOptions: EmailOptions = {
          template: {
            subject: template.subject,
            html: template.html,
          },
          replyTo: userEmail,
        };

        const requestData: NotificationsRequestData = {
          contactIds: [contactId],
          emailOptions,
          sendEmail: true,
          sendSMS: false,
        };

        const response = await sendNotificationFunction(requestData);

        if (response.data.success && response.data.data.totalSent > 0) {
          // Update tracking in Firestore
          const now = Timestamp.now();
          const inviteRef = doc(db, 'events', eventId, 'clientInvites', contactId);
          const existing = inviteRecords[contactId];

          const updatedRecord: ClientInviteRecord = {
            sentCount: (existing?.sentCount || 0) + 1,
            lastSentAt: now,
            history: [...(existing?.history || []), { sentAt: now, sentBy: userEmail }],
          };

          await setDoc(inviteRef, updatedRecord);

          setInviteRecords((prev) => ({ ...prev, [contactId]: updatedRecord }));

          notifications.show('Onboarding invite sent', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show('Failed to send onboarding invite', { severity: 'error' });
        }
      } catch (error) {
        console.error('Error sending onboarding invite:', error);
        notifications.show('Error sending onboarding invite', { severity: 'error' });
      } finally {
        setSendingContactId(null);
      }
    },
    [eventId, buildOnboardingEmail, inviteRecords, notifications]
  );

  // Helper: get email for a contact
  const getContactEmail = useCallback(
    (contactId: string): string | undefined => {
      const contact = contactsData[contactId];
      if (!contact) return undefined;
      return contact.emails?.find((e) => e.is_primary)?.address || contact.emails?.[0]?.address;
    },
    [contactsData]
  );

  // Helper: check if a contact's account exists in Firebase Auth
  const hasAccount = useCallback(
    (contactId: string): boolean | undefined => {
      const email = getContactEmail(contactId);
      if (!email) return undefined;
      if (!(email in authStatuses)) return undefined; // still loading
      return authStatuses[email];
    },
    [getContactEmail, authStatuses]
  );

  return {
    inviteRecords,
    authStatuses,
    isLoadingInvites,
    isCheckingAuth,
    sendingContactId,
    sendOnboardingInvite,
    getContactEmail,
    hasAccount,
  };
}
